import {Inject, Injectable, Optional} from '@angular/core';
import {BehaviorSubject, combineLatest, of, Subject, zip} from 'rxjs';
import {Filter, Pagination, RestService as SimpleRestService, Sort} from '../../../../lib/yii2-rest/rest.service';
import {debounceTime, distinctUntilChanged, map, startWith, switchMap} from 'rxjs/operators';
import {GRID_FILTER_FIELDS} from './grid-filter.config';
import {GridFilterFieldEntities, GridFilterFieldEntity} from './grid-filter-field-entity';
import {ImmutableListRepositoryService} from '../../../../lib/gelios-field/immutable-list-repository.service';
import {GRID_FILTER_RULES} from './grid-filter-rules.config';
import {WeeklyScheduleModel} from '../../models/weekly-schedule.model';
import {ProjectBusinessLogicSyncFilterService} from '../../project-business-logic-filter/project-business-logic-sync-filter.service';

export const GRID_FILTER_SERVICE_DEPS = [
  GRID_FILTER_FIELDS,
  [new Optional(), GRID_FILTER_RULES],
  SimpleRestService,
  ImmutableListRepositoryService,
  ProjectBusinessLogicSyncFilterService
];

export interface ValueLabelItem {
  value: number | string;
  label: string;
}

export interface ValueLabelProjectNameItem {
  value: number;
  label: string;
  project_custom_name: string;
}

@Injectable()
export class GridFilterService {
  projectList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  projectCountryList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  componentTypeList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  warehouseItemTypeList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  warehouseTypeList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  warehouseModelList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  componentModelList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  branchOfficeList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  legalEntityList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  // unitModelList: Observable<ValueLabelItem[]>;
  unitTypeList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  unitModelList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  regionList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  unitGroupList: BehaviorSubject<Array<ValueLabelProjectNameItem>> = new BehaviorSubject<Array<ValueLabelProjectNameItem>>([]);
  creatorList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  integratorList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  businessLogicList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  projectEndUserLevelList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  combinedRoleList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  weeklyScheduleList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  unitHwModelList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  unitCommandNameList: BehaviorSubject<Array<ValueLabelItem>> = new BehaviorSubject<Array<ValueLabelItem>>([]);
  selectedProjectIdChanged = new BehaviorSubject<string | null>(null);
  selectedUnitTypeIdChanged: Subject<number> = new Subject<number>();
  selectedProjectCountryIdChanged: Subject<number> = new Subject<number>();
  selectedRegionIdChanged: Subject<number> = new Subject<number>();
  selectedCreatorIdChanged: Subject<number> = new Subject<number>();
  selectedWarehouseTypeIdChanged: Subject<number> = new Subject<number>();
  selectedWarehouseItemTypeIdChanged: Subject<number> = new Subject<number>();

  constructor(
    @Inject(GRID_FILTER_FIELDS) private gridFilterFields: Array<GridFilterFieldEntity>,
    @Optional() @Inject(GRID_FILTER_RULES) private rules: Record<string, any>,
    protected simpleRestService: SimpleRestService,
    protected immutableList: ImmutableListRepositoryService,
    protected projectBusinessLogicSyncFilterService: ProjectBusinessLogicSyncFilterService,
  ) {
    this.initFilters();
    this.projectBusinessLogicSyncFilterService.getFormattedProjectBusinessLogicFilter()
      .subscribe(filter => {
        // this.selectedProjectIdChanged.next(
        //   filter.project_id != null ? String(filter.project_id) : null
        // );
        // TODO
      });
  }

  buildFilter(entity: string): any {
    const rule = this.rules[entity] || null;
    return rule || {};
  }

  initFilters() {
    if (this.gridFilterFields.includes(GridFilterFieldEntities.PROJECT)) {
      this.simpleRestService.list<any>(
        'project',
        new Pagination(1, 1000),
        new Filter(
          'ProjectSearch',
          {}
        ),
        new Sort('custom_name'),
      ).subscribe(
        page => this.projectList.next(
          page.items.map(item => ({label: item.custom_name, value: item.id.toString()}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.UNIT_TYPE)) {
      this.simpleRestService.list<any>(
        'unit-type',
        new Pagination(1, 1000),
        new Filter(
          'UnitTypeSearch',
          {}
        ),
        new Sort('name'),
      ).subscribe(
        page => this.unitTypeList.next(
          page.items.map(item => ({label: item.translated_description, value: item.id.toString()}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.PROJECT_COUNTRY)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'project-country',
              new Pagination(1, 1000),
              new Filter('ProjectCountrySearch', f),
              new Sort('country_code'),
              'id,name,project.custom_name,country_code',
              'project,country'
            );
          }
        )
      ).subscribe(
        page => this.projectCountryList.next(
          page.items.map(item => ({label: item.country.name, value: item.id.toString(), project_custom_name: item.project.custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.COMPONENT_TYPE)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'component-type',
              new Pagination(1, 1000),
              new Filter('ComponentTypeSearch', f),
              new Sort('name'),
              'id,name,project_custom_name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.componentTypeList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.WAREHOUSE_ITEM_TYPE)) {
      this.immutableList.getList('warehouse-item-type').pipe(
        map(items => items.map(item => ({
          label: item.name,
          value: item.id.toString()
        })))
      ).subscribe(mappedItems => {
        this.warehouseItemTypeList.next(mappedItems);
      });
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.WAREHOUSE_TYPE)) {
      combineLatest(
        this.selectedProjectIdChanged,
        this.selectedWarehouseItemTypeIdChanged.pipe(startWith(null)),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, itemTypeId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (itemTypeId) {
              f['item_type_id'] = itemTypeId;
            }
            return this.simpleRestService.list<any>(
              'warehouse-type',
              new Pagination(1, 1000),
              new Filter('WarehouseTypeSearch', f),
              new Sort('name'),
              'id,name',
            );
          }
        )
      ).subscribe(
        page => this.warehouseTypeList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString()}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.WAREHOUSE_MODEL)) {
      combineLatest(
        this.selectedProjectIdChanged,
        this.selectedWarehouseItemTypeIdChanged.pipe(startWith(null)),
        this.selectedWarehouseTypeIdChanged.pipe(startWith(null)),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, itemTypeId, warehouseTypeId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (itemTypeId) {
              f['item_type_id'] = itemTypeId;
            }
            if (warehouseTypeId) {
              f['warehouse_type_id'] = warehouseTypeId;
            }
            return this.simpleRestService.list<any>(
              'warehouse-model',
              new Pagination(1, 1000),
              new Filter('WarehouseModelSearch', f),
              new Sort('name'),
              'id,name',
            );
          }
        )
      ).subscribe(
        page => this.warehouseModelList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString()}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.COMPONENT_MODEL)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {
              ...this.buildFilter(GridFilterFieldEntities.COMPONENT_MODEL),
            };
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'component-model',
              new Pagination(1, 1000),
              new Filter('ComponentModelSearch', f),
              new Sort('name'),
              'id,name,project_custom_name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.componentModelList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.BRANCH_OFFICE)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'branch-office',
              new Pagination(1, 1000),
              new Filter('BranchOfficeSearch', f),
              new Sort('name'),
              'id,name,project_custom_name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.branchOfficeList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.LEGAL_ENTITY)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'legal-entity',
              new Pagination(1, 1000),
              new Filter('LegalEntitySearch', f),
              new Sort('name'),
              'id,name,project_custom_name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.legalEntityList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.UNIT_MODEL)) {
      combineLatest(
        this.selectedProjectIdChanged,
        this.selectedUnitTypeIdChanged.pipe(startWith(null)),
      ).pipe(
        distinctUntilChanged(
          (a, b) => a[0] === b[0] && a[1] === b[1]
        ),
        switchMap(
          ([projectId, unitTypeId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (unitTypeId) {
              f['unit_type_id'] = unitTypeId;
            }
            return this.simpleRestService.list<any>(
              'unit-model',
              new Pagination(1, 1000),
              new Filter('UnitModelSearch', f),
              new Sort('name'),
              'id,name,project_custom_name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.unitModelList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.BUSINESS_LOGIC)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'business-logic',
              new Pagination(1, 1000),
              new Filter('BusinessLogicSearch', f),
              new Sort('custom_name'),
              '',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.businessLogicList.next(
          page.items.map(item => ({label: item.custom_name, value: item.id.toString(), project_custom_name: item.project_custom_name}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.PROJECT_END_USER_LEVEL)) {
      this.selectedProjectIdChanged.pipe(
        distinctUntilChanged(),
        switchMap(
          projectId => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<any>(
              'project-end-user-level',
              new Pagination(1, 1000),
              new Filter('ProjectEndUserLevelSearch', f),
            );
          }
        )
      ).subscribe(
        page => this.projectEndUserLevelList.next(
          page.items.map(item => ({label: item.name, value: item.id.toString()}))
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.CREATOR)) {
      combineLatest(
        this.selectedProjectIdChanged.pipe(
          distinctUntilChanged(),
        ),
        this.selectedProjectCountryIdChanged.pipe(
          startWith(null),
          distinctUntilChanged(),
        ),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, projectCountryId]) => {
            const f = {
              role: ['admin', 'integrator'],
            };
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (projectCountryId) {
              f['project_country_id'] = projectCountryId;
            }
            return this.simpleRestService.list<any>(
              'creator',
              new Pagination(1, 2000),
              new Filter('CreatorSearch', f),
              new Sort(),
              'id,login',
            );
          }
        )
      ).subscribe(
        page => this.creatorList.next(
          page.items.map(
            item => ({label: item.login, value: item.id.toString()})
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.INTEGRATOR)) {
      combineLatest(
        this.selectedProjectIdChanged.pipe(
          distinctUntilChanged(),
        ),
        this.selectedProjectCountryIdChanged.pipe(
          startWith(null),
          distinctUntilChanged(),
        ),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, projectCountryId]) => {
            const f = {role: 'integrator'};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (projectCountryId) {
              f['project_country_id'] = projectCountryId;
            }
            return this.simpleRestService.list<any>(
              'creator',
              new Pagination(1, 2000),
              new Filter('CreatorSearch', f),
              new Sort(),
              'id,login',
            );
          }
        )
      ).subscribe(
        page => this.integratorList.next(
          page.items.map(
            item => ({label: item.login, value: item.id.toString()})
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.REGION)) {
      combineLatest(
        this.selectedProjectIdChanged,
        this.selectedProjectCountryIdChanged.pipe(startWith(null)),
        this.selectedCreatorIdChanged.pipe(startWith(null)),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, projectCountryId, ownerId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (projectCountryId) {
              f['project_country_id'] = projectCountryId;
            }
            if (ownerId) {
              f['owner_id'] = ownerId;
            }
            return this.simpleRestService.list<any>(
              'region',
              new Pagination(1, 1000),
              new Filter('RegionSearch', f),
              new Sort('name'),
              'id,name,projectCountry.project.custom_name,country_code',
              'projectCountry.project'
            );
          }
        )
      ).subscribe(
        page => this.regionList.next(
          page.items.map(
            item => ({label: item.name, value: item.id.toString(), project_custom_name: item.projectCountry.project.custom_name})
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.UNIT_GROUP)) {
      combineLatest(
        this.selectedProjectIdChanged,
        this.selectedProjectCountryIdChanged.pipe(startWith(null)),
        this.selectedRegionIdChanged.pipe(startWith(null)),
        this.selectedCreatorIdChanged.pipe(startWith(null)),
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId, projectCountryId, regionId, ownerId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            if (projectCountryId) {
              f['project_country_id'] = projectCountryId;
            }
            if (regionId) {
              f['region_id'] = regionId;
            }
            if (ownerId) {
              f['owner_id'] = ownerId;
            }
            return this.simpleRestService.list<any>(
              'unit-group',
              new Pagination(1, 1000),
              new Filter('UnitGroupSearch', f),
              new Sort('name'),
              'id,name',
              'project_custom_name'
            );
          }
        )
      ).subscribe(
        page => this.unitGroupList.next(
          page.items.map(
            item => ({label: item.name, value: item.id.toString(), project_custom_name: item.project_custom_name})
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.COMBINED_ROLE)) {
      console.log(this.immutableList);
      this.immutableList.getList('role-for-filtering').pipe(
        map(
          roles => roles.map(role => ({label: role.name, value: 'base:' + role.id}))
        ),
        switchMap(
          roles => {
            return zip(
              of(roles),
              this.simpleRestService.list<any>(
                'custom-role',
                new Pagination(1, 1000),
              ).pipe(
                map(
                  customRoleResult => customRoleResult.items.map(cr => ({label: cr.name, value: 'custom:' + cr.id}))
                )
              )
            );
          }
        ),
        map(
          ([baseRoles, customRoles]) => [...baseRoles, ...customRoles]
        )
      ).subscribe(
        roles => this.combinedRoleList.next(roles)
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.WEEKLY_SCHEDULE)) {
      this.simpleRestService.list<WeeklyScheduleModel>(
        'weekly-schedule',
        new Pagination(1, 1000),
      ).subscribe(
        page => this.weeklyScheduleList.next(
          page.items.map(
            item => ({label: item.name, value: item.id.toString()})
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.UNIT_HW_MODEL)) {
      combineLatest(
        this.selectedProjectIdChanged,
      ).pipe(
        debounceTime(200),
        switchMap(
          ([projectId]) => {
            const f = {};
            if (projectId) {
              f['project_id'] = projectId;
            }
            return this.simpleRestService.list<{ name: string }>(
              'unit-hw-model',
              new Pagination(1, 1000),
              new Filter('UnitHwModelSearch', f),
              new Sort('name'),
              'name',
            );
          }
        )
      ).subscribe(
        page => this.unitHwModelList.next(
          page.items.map(
            item => ({
              label: item.name.toString(),
              value: item.name.toLowerCase().toString(),
            })
          )
        )
      );
    }
    if (this.gridFilterFields.includes(GridFilterFieldEntities.UNIT_COMMAND_NAME)) {
      this.simpleRestService.list<WeeklyScheduleModel>(
        'unit-command-name',
        new Pagination(1, 1000),
        new Filter('UnitCommandNameSearch', {}),
        new Sort('name'),
        'name',
      ).subscribe(
        page => this.unitCommandNameList.next(
          page.items.map(
            item => ({label: item.name, value: item.name.toString()})
          )
        )
      );
    }
  }
}
